// Incident reports and lost and found (spec §13, §30, §34; ADR 0015).
//
// Incident information is highly restricted. What this file defines is
// shaped so that a view never carries more than its reader may see: the
// reporter's own view, the read view of a confidential incident and the
// full view are separate shapes, and the restricted parts are absent, not
// blank, when they aren't allowed.
//
// Nothing about an incident goes into search, My work, Home, another
// record's activity, or the text of a notification. A notice only ever
// says INCIDENT_NOTICE_TITLE, with a link.

import { z } from 'zod'

const ID = z.string().min(1).max(100)
const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
// Venue-local date and time, as a phone's datetime-local input gives it.
export const LOCAL_DATETIME_PATTERN = /^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d$/
const LOCAL = z.string().regex(LOCAL_DATETIME_PATTERN, 'Use YYYY-MM-DDTHH:MM')
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()

// The only words a notification about an incident ever carries.
export const INCIDENT_NOTICE_TITLE = 'An incident report needs your attention'

// The scheduled job that sends the overdue notices signs as this subject,
// with no person. Core lets it do nothing else.
export const INCIDENTS_SYSTEM_SUB = 'system:incidents'

// Critical and high incidents not reviewed within this many hours are
// overdue.
export const INCIDENT_REVIEW_HOURS = 24

export const INCIDENT_TYPES = ['injury_medical', 'security_ejection', 'property_damage', 'theft', 'near_miss', 'guest_complaint', 'conduct', 'other'] as const
export type IncidentType = (typeof INCIDENT_TYPES)[number]
export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  injury_medical: 'Injury or medical',
  security_ejection: 'Security or ejection',
  property_damage: 'Property damage',
  theft: 'Theft',
  near_miss: 'Near miss',
  guest_complaint: 'Guest complaint',
  conduct: 'Staff or volunteer conduct',
  other: 'Other',
}

export const INCIDENT_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number]
export const INCIDENT_SEVERITY_LABELS: Record<IncidentSeverity, string> = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' }

export const INCIDENT_STATUSES = ['new', 'under_review', 'action_needed', 'closed'] as const
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number]
export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = { new: 'New', under_review: 'Under review', action_needed: 'Action needed', closed: 'Closed' }

export const INCIDENT_PERSON_ROLES = ['injured', 'witness', 'involved', 'staff_responding'] as const
export type IncidentPersonRole = (typeof INCIDENT_PERSON_ROLES)[number]
export const INCIDENT_PERSON_ROLE_LABELS: Record<IncidentPersonRole, string> = { injured: 'Injured', witness: 'Witness', involved: 'Involved', staff_responding: 'Staff responding' }

export const INJURY_TREATMENTS = ['none', 'first_aid', 'ems', 'hospital'] as const
export type InjuryTreatment = (typeof INJURY_TREATMENTS)[number]
export const INJURY_TREATMENT_LABELS: Record<InjuryTreatment, string> = { none: 'None', first_aid: 'First aid', ems: 'EMS', hospital: 'Hospital' }

export const AMENDMENT_FIELDS = ['narrative', 'immediate_actions', 'occurred_at', 'type', 'add_person', 'retract_person'] as const
export type AmendmentField = (typeof AMENDMENT_FIELDS)[number]
export const AMENDMENT_FIELD_LABELS: Record<AmendmentField, string> = {
  narrative: 'What happened',
  immediate_actions: 'Immediate actions',
  occurred_at: 'When it happened',
  type: 'Type',
  add_person: 'Person added',
  retract_person: 'Person retracted',
}

// Overdue: critical or high, still new, reported more than 24 hours ago.
export function isIncidentOverdue(i: { severity: IncidentSeverity; status: IncidentStatus; reportedAt: string }, now: Date = new Date()): boolean {
  return (i.severity === 'critical' || i.severity === 'high') && i.status === 'new' && now.getTime() - Date.parse(i.reportedAt) > INCIDENT_REVIEW_HOURS * 3600_000
}

export const incidentLabel = (number: number) => `INC-${number}`

// ------------------------------------------------------------------ shapes

export type IncidentPerson = {
  id: string
  personId: string | null
  personName: string | null
  name: string | null
  role: IncidentPersonRole
  detail: string | null
  // Added by an amendment after the report was submitted.
  addedByAmendmentId: string | null
  retractedByAmendmentId: string | null
}

export type IncidentAmendment = {
  id: string
  field: AmendmentField
  newValue: string | null
  personRowId: string | null
  reason: string
  createdByName: string | null
  createdAt: string
}

export type IncidentNote = { id: string; body: string; createdByName: string | null; createdAt: string }

export type IncidentFile = { id: string; filename: string | null; contentType: string; sizeBytes: number; classification: 'confidential' | 'internal'; uploadedByName: string | null; uploadedAt: string }

export type IncidentFollowUp = { kind: 'work_order' | 'task'; id: string; label: string; status: string }

// What a list shows. No narrative, no people: just enough to triage.
export type IncidentSummary = {
  id: string
  number: number
  status: IncidentStatus
  type: IncidentType
  severity: IncidentSeverity
  occurredLocal: string
  reportedAt: string
  facilityName: string | null
  spaceName: string | null
  eventName: string | null
  confidential: boolean
  investigatorName: string | null
  overdue: boolean
  // True when the viewer filed it.
  mine: boolean
  // Disposed under the retention policy: only the tombstone is left.
  disposed: boolean
  legalHold: boolean
}

// Who sees what of one incident:
//   access 'full'        incidents.manage, or incidents.read on one that
//                        isn't confidential: everything but the notes
//                        (notes are manage only)
//   access 'restricted'  incidents.read on a confidential one: the
//                        summary, and that it is confidential
//   access 'reporter'    its reporter with nothing more: the report as
//                        they filed it, their own amendments and files,
//                        and its status
export type IncidentAccess = 'full' | 'restricted' | 'reporter'

export type Incident = IncidentSummary & {
  access: IncidentAccess
  facilityId: string | null
  spaceId: string | null
  eventId: string | null
  locationDetail: string | null
  reporterName: string | null
  reporterPersonId: string
  onBehalfOf: string | null
  updatedAt: string
  version: number
  can: { manage: boolean; amend: boolean; attach: boolean }
  // Retention (manage only): when it falls due for review, the legal hold,
  // and the disposal, once it happened.
  retention?: IncidentRetention
  // Absent when access is 'restricted'.
  report?: {
    reportedType: IncidentType
    reportedOccurredLocal: string
    narrative: string
    immediateActions: string | null
    emsCalled: boolean
    policeCalled: boolean
    agencyReference: string | null
    injuryBodyPart: string | null
    injuryTreatment: InjuryTreatment | null
    assetId: string | null
    assetName: string | null
    assetTag: string | null
    people: IncidentPerson[]
    amendments: IncidentAmendment[]
    files: IncidentFile[]
  }
  // Only with 'full' access.
  investigation?: {
    investigatorPersonId: string | null
    reviewedAt: string | null
    rootCause: string | null
    correctiveAction: string | null
    closedNoneReason: string | null
    closedAt: string | null
    closedByName: string | null
    followUps: IncidentFollowUp[]
  }
  // Only with incidents.manage.
  notes?: IncidentNote[]
}

// ------------------------------------------------------------------ bodies

const PersonEntry = z
  .object({
    personId: ID.nullable().optional(),
    name: optionalText(200),
    role: z.enum(INCIDENT_PERSON_ROLES),
    detail: optionalText(1000),
  })
  .refine((p) => !!p.personId || !!p.name, { message: 'Choose a person or give a name' })

// Filing a report. Only when, what kind and what happened are required;
// everything else can be filled in later by whoever manages it.
export const IncidentReportBody = z
  .object({
    occurredLocal: LOCAL,
    type: z.enum(INCIDENT_TYPES),
    narrative: text(20000).min(1, 'Say what happened'),
    severity: z.enum(INCIDENT_SEVERITIES).optional(),
    facilityId: ID.nullable().optional(),
    spaceId: ID.nullable().optional(),
    locationDetail: optionalText(500),
    eventId: ID.nullable().optional(),
    immediateActions: optionalText(5000),
    onBehalfOf: optionalText(200),
    people: z.array(PersonEntry).max(50).optional(),
    emsCalled: z.boolean().optional(),
    policeCalled: z.boolean().optional(),
    agencyReference: optionalText(200),
    injuryBodyPart: optionalText(200),
    injuryTreatment: z.enum(INJURY_TREATMENTS).nullable().optional(),
    confidential: z.boolean().optional(),
  })
  .refine((b) => b.type === 'injury_medical' || (!b.injuryBodyPart && !b.injuryTreatment), { message: 'Injury details go only on an injury or medical incident' })

// Triage and investigation, by whoever manages incidents. The submitted
// report is not in here: it changes only through an amendment.
export const IncidentPatch = z.object({
  status: z.enum(['new', 'under_review', 'action_needed']).optional(),
  severity: z.enum(INCIDENT_SEVERITIES).optional(),
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
  locationDetail: optionalText(500),
  eventId: ID.nullable().optional(),
  assetId: ID.nullable().optional(),
  emsCalled: z.boolean().optional(),
  policeCalled: z.boolean().optional(),
  agencyReference: optionalText(200),
  injuryBodyPart: optionalText(200),
  injuryTreatment: z.enum(INJURY_TREATMENTS).nullable().optional(),
  confidential: z.boolean().optional(),
  investigatorPersonId: ID.nullable().optional(),
  rootCause: optionalText(5000),
  correctiveAction: optionalText(5000),
  expectedVersion: z.number().int().min(1),
})

// Closing needs a root cause and a corrective action, or an explicit
// "none" with the reason.
export const CloseIncidentBody = z
  .object({
    rootCause: optionalText(5000),
    correctiveAction: optionalText(5000),
    noneReason: optionalText(2000),
    expectedVersion: z.number().int().min(1),
  })
  .refine((b) => (!!b.rootCause && !!b.correctiveAction) || !!b.noneReason, { message: 'Give the root cause and the corrective action, or say why there are none' })

export const ReopenIncidentBody = z.object({ reason: text(2000).min(1), expectedVersion: z.number().int().min(1) })

export const AmendmentBody = z
  .object({
    field: z.enum(AMENDMENT_FIELDS),
    newValue: optionalText(20000),
    person: PersonEntry.optional(),
    personRowId: ID.optional(),
    reason: text(2000).min(1, 'Say why'),
  })
  .superRefine((b, ctx) => {
    if (b.field === 'type' && !(INCIDENT_TYPES as readonly string[]).includes(b.newValue ?? '')) ctx.addIssue({ code: 'custom', message: 'Choose the corrected type' })
    if (b.field === 'occurred_at' && !LOCAL_DATETIME_PATTERN.test(b.newValue ?? '')) ctx.addIssue({ code: 'custom', message: 'Give the corrected date and time' })
    if ((b.field === 'narrative' || b.field === 'immediate_actions') && !b.newValue) ctx.addIssue({ code: 'custom', message: 'Write the correction or addition' })
    if (b.field === 'add_person' && !b.person) ctx.addIssue({ code: 'custom', message: 'Name the person to add' })
    if (b.field === 'retract_person' && !b.personRowId) ctx.addIssue({ code: 'custom', message: 'Choose who to retract' })
  })

export const IncidentNoteBody = z.object({ body: text(10000).min(1) })

export const IncidentWorkOrderBody = z.object({
  title: text(300).min(1),
  description: optionalText(4000),
  type: z.enum(['repair', 'damage', 'other']).default('repair'),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  dueOn: DAY.nullable().optional(),
})

export const IncidentTaskBody = z.object({
  title: text(300).min(1),
  description: optionalText(4000),
  assigneePersonId: ID.nullable().optional(),
  dueOn: DAY.nullable().optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
})

// What the report page needs to fill in "where" and "which event" for
// someone who may only file reports: every active facility and space, and
// the events on around today.
export type IncidentReportOptions = {
  today: string
  facilities: { id: string; name: string; spaces: { id: string; name: string }[] }[]
  events: { id: string; name: string; startsOn: string; endsOn: string }[]
}

// For a person's page, to incidents.read: how many incidents name them,
// and a link to each. Nothing else.
export type PersonIncidentLink = { id: string; number: number; occurredLocal: string; status: IncidentStatus; roles: IncidentPersonRole[] }

// purge: files of disposed incidents whose bytes the Worker still has to
// delete (a retry of any it missed when the incident was disposed).
export type IncidentsRunResult = { overdueNoticed: number; errors: string[]; purge?: { fileId: string; r2Key: string }[] }

// ------------------------------------------------------------ retention
//
// The owner set incident retention on 2026-10-01: an incident becomes due
// for retention review 7 years after it was closed (a setting, not a
// constant), never while open, never while on legal hold. Nothing is
// deleted automatically; disposal is a manager's action with a reason and
// leaves a tombstone. Injuries involving minors may need longer under Iowa
// law, which is what the legal hold is for.

export const DEFAULT_INCIDENT_RETENTION_YEARS = 7
export const INCIDENT_TOMBSTONE_NARRATIVE = '[Disposed under the incident retention policy]'
export const INCIDENT_TOMBSTONE_REASON = 'Disposed under the incident retention policy'
export const LEGAL_HOLD_NOTE = 'Hold an incident that may be needed longer: a claim, a lawsuit, an insurance matter, or an injury to a minor (Iowa law can extend the time to sue until after a minor turns 18).'

// The day an incident closed (its UTC date, as the database compares it)
// plus the retention years. February 29 rolls to March 1, as SQLite's
// date() does.
export function incidentRetentionDueOn(closedAt: string, years: number): string {
  const [y, m, d] = closedAt.slice(0, 10).split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y + years, m - 1, d)).toISOString().slice(0, 10)
}

export type IncidentRetention = {
  years: number
  // Null while open.
  dueOn: string | null
  due: boolean
  legalHold: { reason: string | null; at: string | null; byName: string | null } | null
  disposed: { at: string; byName: string | null; reason: string | null } | null
}

export type RetentionDueIncident = IncidentSummary & { closedAt: string; dueOn: string }

export const LegalHoldBody = z.object({ hold: z.boolean(), reason: z.string().trim().min(3).max(500) })
export const DisposeIncidentBody = z.object({ reason: z.string().trim().min(3).max(500), expectedVersion: z.number().int().min(1) })
export const IncidentRetentionSettingsBody = z.object({ retentionYears: z.number().int().min(1).max(100) })

// ================================================================ lost and found

export const LOST_FOUND_CATEGORIES = ['phone', 'wallet_id', 'keys', 'bag', 'clothing', 'jewelry', 'other'] as const
export type LostFoundCategory = (typeof LOST_FOUND_CATEGORIES)[number]
export const LOST_FOUND_CATEGORY_LABELS: Record<LostFoundCategory, string> = {
  phone: 'Phone',
  wallet_id: 'Wallet or ID',
  keys: 'Keys',
  bag: 'Bag',
  clothing: 'Clothing',
  jewelry: 'Jewelry',
  other: 'Other',
}
// Always high value: their contents are logged with a second staff member.
export const HIGH_VALUE_CATEGORIES: LostFoundCategory[] = ['phone', 'wallet_id', 'jewelry']
export const HIGH_VALUE_HANDLING = 'Log its contents with a second staff member present, and keep ID and card details off every list.'

export const FOUND_ITEM_STATUSES = ['in_storage', 'claimed', 'returned', 'disposed', 'donated', 'police'] as const
export type FoundItemStatus = (typeof FOUND_ITEM_STATUSES)[number]
export const FOUND_ITEM_STATUS_LABELS: Record<FoundItemStatus, string> = {
  in_storage: 'In storage',
  claimed: 'Claimed',
  returned: 'Returned',
  disposed: 'Disposed',
  donated: 'Donated',
  police: 'Turned over to police',
}
export const FOUND_ITEM_HELD_STATUSES: FoundItemStatus[] = ['in_storage', 'claimed']
export const DISPOSAL_METHODS = ['disposed', 'donated', 'police'] as const
export type DisposalMethod = (typeof DISPOSAL_METHODS)[number]

export const DEFAULT_HOLD_DAYS = 60

export const foundItemLabel = (number: number) => `LF-${number}`
export const lostReportLabel = (number: number) => `LR-${number}`

export type FoundItemSummary = {
  id: string
  number: number
  foundLocal: string
  category: LostFoundCategory
  // Safe for a list: what it looks like, never what is in it.
  description: string
  highValue: boolean
  status: FoundItemStatus
  facilityName: string | null
  spaceName: string | null
  eventName: string | null
  storageLocation: string | null
  // Days in storage, for the hold period.
  daysHeld: number
  readyToDispose: boolean
  photoFileId: string | null
}

export type FoundItemClaim = {
  id: string
  claimantName: string
  claimantContact: string | null
  claimantPersonId: string | null
  claimantPersonName: string | null
  claimantDescription: string
  status: 'pending' | 'released' | 'declined'
  verificationNote: string | null
  signatureAck: boolean
  releasedByName: string | null
  releasedAt: string | null
  declinedNote: string | null
  createdByName: string | null
  createdAt: string
}

export type FoundItem = FoundItemSummary & {
  facilityId: string | null
  spaceId: string | null
  eventId: string | null
  locationDetail: string | null
  // Seen only on the item's own page, never in a list or search.
  contentsNote: string | null
  contentsWitnessName: string | null
  foundByPersonId: string | null
  foundByName: string | null
  disposedAt: string | null
  disposedByName: string | null
  disposalNote: string | null
  createdByName: string | null
  createdAt: string
  updatedAt: string
  version: number
  photos: { id: string; filename: string | null; uploadedAt: string }[]
  claims: FoundItemClaim[]
  matchedLostReports: { id: string; number: number; guestName: string }[]
  can: { manage: boolean }
}

export type LostReport = {
  id: string
  number: number
  lostOn: string
  category: LostFoundCategory
  description: string
  facilityId: string | null
  facilityName: string | null
  spaceId: string | null
  spaceName: string | null
  locationDetail: string | null
  eventId: string | null
  eventName: string | null
  guestName: string
  // Only on the report's own page.
  guestContact?: string | null
  guestPersonId: string | null
  status: 'open' | 'matched' | 'closed'
  matchedFoundItemId: string | null
  matchedFoundItemNumber: number | null
  closedNote: string | null
  createdByName: string | null
  createdAt: string
}

const CATEGORY = z.enum(LOST_FOUND_CATEGORIES)

export const FoundItemBody = z.object({
  foundLocal: LOCAL,
  category: CATEGORY,
  description: text(1000).min(1),
  highValue: z.boolean().optional(),
  contentsNote: optionalText(2000),
  contentsWitnessPersonId: ID.nullable().optional(),
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
  locationDetail: optionalText(500),
  eventId: ID.nullable().optional(),
  storageLocation: optionalText(200),
  foundByPersonId: ID.nullable().optional(),
  foundByName: optionalText(200),
})

export const FoundItemPatch = FoundItemBody.partial().extend({ status: z.enum(['in_storage', 'claimed']).optional(), expectedVersion: z.number().int().min(1) })

export const ClaimBody = z.object({
  claimantName: text(200).min(1),
  claimantContact: optionalText(300),
  claimantPersonId: ID.nullable().optional(),
  claimantDescription: text(2000).min(1, 'Write down how they described it'),
  // Hand it over now. Needs the verification note and the acknowledgment.
  release: z.boolean().optional(),
  verificationNote: optionalText(2000),
  signatureAck: z.boolean().optional(),
})

export const ReleaseClaimBody = z.object({ verificationNote: text(2000).min(1, 'Say how ownership was verified'), signatureAck: z.literal(true, { message: 'The claimant must acknowledge receipt' }) })
export const DeclineClaimBody = z.object({ note: text(2000).min(1, 'Say why') })

export const DisposeBody = z.object({ method: z.enum(DISPOSAL_METHODS), note: optionalText(2000) })

export const LostReportBody = z.object({
  lostOn: DAY,
  category: CATEGORY,
  description: text(1000).min(1),
  guestName: text(200).min(1),
  guestContact: optionalText(300),
  guestPersonId: ID.nullable().optional(),
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
  locationDetail: optionalText(500),
  eventId: ID.nullable().optional(),
})

export const MatchLostReportBody = z.object({ foundItemId: ID })
export const CloseLostReportBody = z.object({ note: text(2000).min(1) })
export const LostFoundSettingsBody = z.object({ holdDays: z.number().int().min(0).max(3650) })

// A suggestion only: same category, found within LOST_MATCH_WINDOW of the
// day it was lost, and the same facility when both say one. Staff decide.
export const LOST_MATCH_DAYS_BEFORE = 2
export const LOST_MATCH_DAYS_AFTER = 7

// Venue-local (America/Chicago) date and time now, YYYY-MM-DDTHH:MM, as a
// datetime-local input shows it.
export function chicagoLocalNow(at: Date = new Date()): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}
